import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Spinner from "../../components/Spinner";
import DatePicker from "../../components/DatePicker2";
import { getVariantInventory } from "../../distflowAPI/products";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const formatQty = (val) => {
  if (val == null) return "—";
  return Number(val).toLocaleString("en-US");
};

const InventoryForecast = ({ variantId, trackInZoho }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [asOf, setAsOf] = useState(null);

  const fetchInventory = useCallback(async () => {
    if (!variantId || !trackInZoho) return;
    setLoading(true);
    setError(null);
    try {
      const dateStr = asOf ? dayjs(asOf).format("YYYY-MM-DD") : undefined;
      const res = await getVariantInventory(variantId, dateStr);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setError("This variant is not tracked in Zoho Inventory.");
      } else if (err.response?.status === 502) {
        setError("Zoho Inventory is currently unreachable. Please try again later.");
      } else {
        setError("Failed to load inventory forecast.");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [variantId, trackInZoho, asOf]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  if (!trackInZoho) {
    return (
      <Typography sx={{ color: "#666" }}>
        Enable "Track in Zoho" to view inventory forecast.
      </Typography>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f6f6f79e",
            zIndex: 5,
          }}
        >
          <Spinner />
        </Box>
      )}
      <Box mb={2} maxWidth={260}>
        <DatePicker
          size="small"
          controlledValue={asOf}
          setControlledValue={setAsOf}
          label="As of date"
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {data && (
        <>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  {data.horizons.map((h) => (
                    <TableCell key={h.date} align="right">
                      <Typography fontWeight={600} fontSize="13px">
                        {h.label}
                      </Typography>
                      <Typography fontSize="11px" sx={{ color: "#666" }}>
                        {h.date}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="13px">On Hand</Typography>
                  </TableCell>
                  {data.horizons.map((h, i) => (
                    <TableCell key={h.date} align="right">
                      {i === 0 ? formatQty(data.on_hand) : "—"}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="13px">Projected</Typography>
                  </TableCell>
                  {data.horizons.map((h) => (
                    <TableCell key={h.date} align="right">
                      {formatQty(h.projected)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <ActivityDetail title="Incoming POs" items={data.incoming_pos} />
          <ActivityDetail title="Outgoing SOs" items={data.outgoing_sos} />
        </>
      )}
    </Box>
  );
};

const ActivityDetail = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Accordion disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36 }}>
        <Typography fontWeight={600} fontSize="13px">
          {title} ({items.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell>Reference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>{item.date}</TableCell>
                <TableCell align="right">{formatQty(item.qty)}</TableCell>
                <TableCell>{item.ref}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

export default InventoryForecast;

